import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Alert,
  Autocomplete,
  CircularProgress,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  styled,
  alpha,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckIcon from '@mui/icons-material/Check';
import KeyIcon from '@mui/icons-material/Key';
import axiosInstance from '../../lib/axios';
import { useSnackbar } from '../../components/SnackbarProvider';
import type { CreateLicenseDto, GeneratedLicense, LicenseStatus } from '../../types/licenses';

interface Organization {
  id: string;
  name: string;
}

const HeaderBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
  padding: '48px 40px',
  borderRadius: '16px',
  marginBottom: '32px',
  boxShadow: '0 12px 40px rgba(124, 58, 237, 0.25)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)',
  },
}));

const LicenseCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '12px',
  backgroundColor: alpha('#000', 0.02),
  border: `1px solid ${alpha('#000', 0.08)}`,
}));

const CopyButton = styled(IconButton)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

const fetchOrganizations = async (): Promise<Organization[]> => {
  const response = await axiosInstance.get('/organizations', {
    params: { limit: 1000 },
  });
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchLicenseStatus = async (): Promise<LicenseStatus> => {
  const response = await axiosInstance.get('/licenses/status');
  return response.data;
};

const generateLicense = async (data: CreateLicenseDto): Promise<GeneratedLicense> => {
  const response = await axiosInstance.post('/licenses', data);
  return response.data;
};

export const LicenseGenerate = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [generatedLicense, setGeneratedLicense] = useState<GeneratedLicense | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSignature, setCopiedSignature] = useState(false);

  const { data: organizations = [], isLoading: orgsLoading } = useQuery({
    queryKey: ['organizations-all'],
    queryFn: fetchOrganizations,
  });

  const { data: licenseStatus } = useQuery({
    queryKey: ['license-status'],
    queryFn: fetchLicenseStatus,
  });

  const generateMutation = useMutation({
    mutationFn: generateLicense,
    onSuccess: (data) => {
      setGeneratedLicense(data);
      showMessage('License generated successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to generate license', 'error');
    },
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<CreateLicenseDto>({
    defaultValues: {
      tier: 'professional',
      maxUsers: 5,
      maxLocations: 3,
      durationDays: 365,
    },
  });

  const durationType = watch('durationDays');

  const onSubmit = (data: CreateLicenseDto) => {
    if (!selectedOrg) {
      showMessage('Please select an organization', 'error');
      return;
    }

    const validUntil = new Date();
    validUntil.setDate(validUntil.getDate() + (data.durationDays || 365));

    generateMutation.mutate({
      ...data,
      organizationId: selectedOrg.id,
      organizationName: selectedOrg.name,
      validUntil: validUntil.toISOString(),
    });
  };

  const handleCopyKey = async () => {
    if (generatedLicense) {
      await navigator.clipboard.writeText(generatedLicense.licenseKey);
      setCopiedKey(true);
      showMessage('License key copied to clipboard', 'success');
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  const handleCopySignature = async () => {
    if (generatedLicense) {
      await navigator.clipboard.writeText(generatedLicense.signature);
      setCopiedSignature(true);
      showMessage('Signature copied to clipboard', 'success');
      setTimeout(() => setCopiedSignature(false), 2000);
    }
  };

  const handleDownload = () => {
    if (generatedLicense) {
      const content = `NEXERA Desktop License
========================

Organization: ${generatedLicense.organizationName}
Tier: ${generatedLicense.tier.toUpperCase()}
Valid From: ${new Date(generatedLicense.validFrom).toLocaleDateString()}
Valid Until: ${new Date(generatedLicense.validUntil).toLocaleDateString()}
Max Users: ${generatedLicense.maxUsers}
Max Locations: ${generatedLicense.maxLocations}

LICENSE KEY:
${generatedLicense.licenseKey}

SIGNATURE:
${generatedLicense.signature}

========================
IMPORTANT: Keep this file secure. Both the license key and signature are required for activation.
`;

      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexera-license-${generatedLicense.organizationName.replace(/\s+/g, '-').toLowerCase()}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showMessage('License file downloaded', 'success');
    }
  };

  const handleGenerateAnother = () => {
    setGeneratedLicense(null);
    setSelectedOrg(null);
  };

  if (licenseStatus && !licenseStatus.available) {
    return (
      <Box>
        <HeaderBox>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <KeyIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
              <Typography
                variant="h3"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '36px',
                  letterSpacing: '-0.02em',
                }}
              >
                Generate License
              </Typography>
            </Box>
          </Box>
        </HeaderBox>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            License Generation Not Configured
          </Typography>
          <Typography variant="body2">
            {licenseStatus.message}
          </Typography>
        </Alert>
      </Box>
    );
  }

  if (generatedLicense) {
    return (
      <Box>
        <HeaderBox>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <KeyIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
              <Typography
                variant="h3"
                sx={{
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '36px',
                  letterSpacing: '-0.02em',
                }}
              >
                License Generated
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '15px',
              }}
            >
              Provide both the license key and signature to the customer
            </Typography>
          </Box>
        </HeaderBox>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Organization
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {generatedLicense.organizationName}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Tier
                </Typography>
                <Typography variant="body1" fontWeight={600} textTransform="capitalize">
                  {generatedLicense.tier}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Valid Until
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {new Date(generatedLicense.validUntil).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">
                  Limits
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {generatedLicense.maxUsers} users, {generatedLicense.maxLocations} locations
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  License Key
                </Typography>
                <Tooltip title={copiedKey ? 'Copied!' : 'Copy to clipboard'}>
                  <CopyButton size="small" onClick={handleCopyKey}>
                    {copiedKey ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </CopyButton>
                </Tooltip>
              </Box>
              <LicenseCard>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: '12px',
                  }}
                >
                  {generatedLicense.licenseKey}
                </Typography>
              </LicenseCard>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Signature
                </Typography>
                <Tooltip title={copiedSignature ? 'Copied!' : 'Copy to clipboard'}>
                  <CopyButton size="small" onClick={handleCopySignature}>
                    {copiedSignature ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                  </CopyButton>
                </Tooltip>
              </Box>
              <LicenseCard>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    fontSize: '12px',
                    maxHeight: '100px',
                    overflow: 'auto',
                  }}
                >
                  {generatedLicense.signature}
                </Typography>
              </LicenseCard>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              Provide both the License Key AND Signature to the customer for desktop app activation.
            </Alert>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
              >
                Download as File
              </Button>
              <Button
                variant="outlined"
                onClick={handleGenerateAnother}
              >
                Generate Another
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/licenses')}
              >
                Back to Licenses
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <HeaderBox>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <KeyIcon sx={{ fontSize: 40, color: '#fff', opacity: 0.9 }} />
            <Typography
              variant="h3"
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '36px',
                letterSpacing: '-0.02em',
              }}
            >
              Generate Desktop License
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              fontSize: '15px',
              maxWidth: '600px',
            }}
          >
            Create a cryptographically signed license for desktop app activation
          </Typography>
        </Box>
      </HeaderBox>

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Organization
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={organizations}
                  getOptionLabel={(option) => option.name}
                  value={selectedOrg}
                  onChange={(_, newValue) => setSelectedOrg(newValue)}
                  loading={orgsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Organization"
                      required
                      error={!selectedOrg && generateMutation.isPending}
                      helperText={!selectedOrg ? 'Organization is required' : ''}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {orgsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  License Configuration
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={!!errors.tier}>
                  <InputLabel>License Tier</InputLabel>
                  <Controller
                    name="tier"
                    control={control}
                    rules={{ required: 'Tier is required' }}
                    render={({ field }) => (
                      <Select {...field} label="License Tier">
                        <MenuItem value="starter">Starter</MenuItem>
                        <MenuItem value="professional">Professional</MenuItem>
                        <MenuItem value="enterprise">Enterprise</MenuItem>
                      </Select>
                    )}
                  />
                  {errors.tier && <FormHelperText>{errors.tier.message}</FormHelperText>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxUsers', {
                    required: 'Max users is required',
                    min: { value: 1, message: 'Must be at least 1' },
                  })}
                  error={!!errors.maxUsers}
                  helperText={errors.maxUsers?.message}
                  fullWidth
                  label="Max Users"
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  {...register('maxLocations', {
                    required: 'Max locations is required',
                    min: { value: 1, message: 'Must be at least 1' },
                  })}
                  error={!!errors.maxLocations}
                  helperText={errors.maxLocations?.message}
                  fullWidth
                  label="Max Locations"
                  type="number"
                  inputProps={{ min: 1 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                  Duration
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Controller
                    name="durationDays"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Duration"
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <MenuItem value={30}>30 Days (Trial)</MenuItem>
                        <MenuItem value={90}>90 Days</MenuItem>
                        <MenuItem value={180}>6 Months</MenuItem>
                        <MenuItem value={365}>1 Year</MenuItem>
                        <MenuItem value={730}>2 Years</MenuItem>
                        <MenuItem value={1095}>3 Years</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Valid Until"
                  type="date"
                  disabled
                  value={(() => {
                    const date = new Date();
                    date.setDate(date.getDate() + (durationType || 365));
                    return date.toISOString().split('T')[0];
                  })()}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box display="flex" gap={2} mt={4}>
              <Button
                type="submit"
                variant="contained"
                startIcon={generateMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={generateMutation.isPending || !selectedOrg}
                sx={{
                  background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #6d28d9 0%, #1d4ed8 100%)',
                  },
                }}
              >
                {generateMutation.isPending ? 'Generating...' : 'Generate License'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/licenses')}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
